import Locale from "./vue-i18n-locales.generated";

const STATUS_KEYS = {
    401: "unauthenticated",
    403: "forbidden",
    404: "not_found",
    419: "session_expired",
    429: "too_many_requests",
    500: "server",
    503: "unavailable",
};

const FALLBACK_LOCALE = "en";
const SAFE_SERVER_MESSAGE_STATUSES = new Set([400, 401, 403, 404, 409, 422, 429]);

function currentLocale() {
    return document.documentElement.lang?.substr(0, 2) || FALLBACK_LOCALE;
}

function translate(key) {
    const lang = currentLocale();
    return (
        Locale[lang]?.general?.errors?.[key] ??
        Locale[FALLBACK_LOCALE]?.general?.errors?.[key] ??
        Locale[FALLBACK_LOCALE]?.general?.errors?.fallback
    );
}

function statusMessage(status) {
    const key = STATUS_KEYS[status];
    return key ? translate(key) : null;
}

function notify(type, message) {
    if (!message) {
        return;
    }

    window.dispatchEvent(
        new CustomEvent("notify", {
            detail: { type, message },
        })
    );
}

function extractServerMessage(body, status) {
    if (!SAFE_SERVER_MESSAGE_STATUSES.has(status)) {
        return null;
    }

    try {
        const parsed = JSON.parse(body);
        const message = parsed?.message;
        return typeof message === "string" && message.length > 0 ? message : null;
    } catch {
        return null;
    }
}

/**
 * Whether every failure of this request is already shown next to what caused
 * it (see showErrorsInline in alpine/action-button.js), so no toast is added.
 */
export function errorsShownInline(request) {
    const messages = Array.from(request.messages ?? []);

    return messages.length > 0 && messages.every((message) => message.errorsShownInline === true);
}

window.notify = notify;

document.addEventListener("livewire:init", () => {
    Livewire.interceptRequest(({ request, onError, onFailure }) => {
        const activeCompanyId = document.head.querySelector(
            'meta[name="active-company-id"]'
        )?.content;

        if (activeCompanyId) {
            request.options.headers["X-Salesbyte-Active-Company-Id"] =
                activeCompanyId;
        }

        onError(({ response, body, preventDefault }) => {
            const status = response.status;

            if (status === 422) {
                return;
            }

            preventDefault();

            if (status === 419) {
                notify("error", translate("session_expired"));
                setTimeout(() => window.location.reload(), 1500);
                return;
            }

            if (errorsShownInline(request)) {
                return;
            }

            const message =
                extractServerMessage(body, status) ??
                statusMessage(status) ??
                translate("fallback");

            notify("error", message);
        });

        onFailure(() => {
            if (errorsShownInline(request)) {
                return;
            }

            notify("error", translate("network"));
        });
    });

    Livewire.on("notify", (payload) => {
        const detail = Array.isArray(payload) ? payload[0] : payload;
        if (!detail) {
            return;
        }
        notify(detail.type ?? "success", detail.message);
    });
});
